/**
 * The HTTP status behind a failed call, however the call reported the failure.
 *
 * Kept out of `api.ts` so that reading a status does not mean loading the dashboard API's client
 * (and the axios instance it builds at import) into every page and test that only needs this.
 */

/**
 * An `Error` that still knows the HTTP status it came from.
 *
 * Some calls in `api.ts` rethrow the backend's message as a plain `Error`, which every caller
 * shows as it is, and in doing so lose the status. This is the same error with the status riding
 * along: the message, `instanceof Error` and `name` are all unchanged, so a caller that reads the
 * message sees exactly what it saw before, and one that must tell a 402 apart can.
 */
export class ApiStatusError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

/**
 * The status from an `ApiStatusError`, or straight off an axios error for the calls that pass
 * those through untouched. `undefined` when the call never got an answer.
 */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (error instanceof ApiStatusError) return error.status;
  const status = (error as { response?: { status?: unknown } } | null)
    ?.response?.status;
  return typeof status === "number" ? status : undefined;
};
